import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { debounceTime } from 'rxjs/operators';
import { formConstant } from '../../../../../config';
import { IAvailableTypes } from '../../../../core/models/available-types.interface';
import { ICountry } from '../../../../core/models/country.interface';
import { PageParams } from '../../../../core/models/page-params.model';
import { IPaginatorProperties } from '../../../../core/models/paginator.interface';
import { ConstantService } from '../../../../core/services/constant.service';
import { NotificationService } from '../../../../core/services/notification.service';
import { UserService } from '../../../../core/services/user.service';
import { ICarrierItem, IStatus, ISubService } from '../../models/shipment-price.interface';
import { ShipmentPriceService } from '../../services/shipment-price.service';
import { CarrierPopupComponent } from './carrier-popup/carrier-popup.component';

@Component({
    selector: 'eci-carriers-list',
    templateUrl: './carriers-list.component.html',
    styleUrls: ['./carriers-list.component.scss']
})
export class CarriersListComponent implements OnInit {

    public sort: Sort;
    public pageParams: PageParams = new PageParams();
    public dataSource = new MatTableDataSource<ICarrierItem>([]);
    public form: FormGroup;
    public showLoader: boolean = false;
    public isDialogOpen: boolean = false;

    carriersList: ICarrierItem[];

    availableTypes: IAvailableTypes;
    public countries: ICountry[] = [];
    public subServices: ISubService[] = [];

    displayedColumns: string[] = [
        'carrierId',
        'carrierName',
        'mode',
        'type',
        'country',
        'contactName',
        'contactTel',
        'contactEmail',
        'subServices',
    ];

    constructor(
        public dialog: MatDialog,
        private shipmentPriceSvc: ShipmentPriceService,
        private notify: NotificationService,
        public userSvc: UserService,
        private constSvc: ConstantService,
        private shipmentPriceService: ShipmentPriceService,
    ) {
    }

    ngOnInit(): void {
        this.form = this.initializeForm();
        if (this.isAdmin) {
            this.displayedColumns.push(...['createdBy', 'active', 'edit', 'delete']);
        }
        this.getParams('sub-service', 'subServices');
        this.getListOfCountries();
        this.getList();

        this.searchSubscriber();

        this.constSvc.availableTypes.subscribe((data) => {
            this.availableTypes = data;
        });
    }

    private initializeForm(): FormGroup {
        this.pageParams.pageSize = formConstant.pageSize;
        this.pageParams.pageIndex = 0;
        return new FormGroup({
            search: new FormControl(''),
        });

    }
    private searchSubscriber(): void {
        this.form.get('search').valueChanges.pipe(debounceTime(500))
            .subscribe(() => {
                this.getList();
            });
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    private getParams(path: string, objectName: string, page: number = 1, limit: number = 1000, q: string = ''): void {
        this.shipmentPriceService.get<IPaginatorProperties<any>>(path, page, limit, q).subscribe((data) => {
            if (!data) {
                return;
            }
            this[objectName] = data.items;
        });
    }

    private getListOfCountries(
        page: number = 1,
        q: string = '',
        limit: number = 1000,
        withTaxRate: boolean = false,
    ): void {
        this.constSvc.getAvailableCountriesForScroll(page, limit, false, q, withTaxRate).subscribe(data => {
            if (data) {
                this.countries = data.items;
            }
        });
    }

    public onUpdateHandler(id: number) {
        if (this.isDialogOpen) {
            return;
        }
        this.isDialogOpen = true;

        this.shipmentPriceSvc.getCarriersById(id).subscribe(data => {
            if (!data) {
                return;
            }
            const dialogRef = this.dialog.open(CarrierPopupComponent, {
                width: '900px',
            });
            dialogRef.componentInstance.info = data;
            dialogRef.componentInstance.isUpdate = true;
            dialogRef.componentInstance.countries = this.countries;
            dialogRef.componentInstance.subServices = this.subServices;
            dialogRef.componentInstance.createMode = false;

            dialogRef.afterClosed().subscribe((result) => {
                this.isDialogOpen = false;
                if (!result) {
                    return;
                }
                this.getList();
                this.dataSource.data = this.dataSource.data;
            });
        });

    }

    openDialog(): void {
        const dialogRef = this.dialog.open(CarrierPopupComponent, {
            width: '900px',
        });
        dialogRef.componentInstance.countries = this.countries;
        dialogRef.componentInstance.subServices = this.subServices;
        dialogRef.componentInstance.createMode = true;

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            this.getList();
            this.dataSource.data = result;
        });
    }

    public getList(
        page: number = this.pageParams?.pageIndex + 1 || 1,
        limit: number = this.pageParams?.pageSize || 100,
        search: string = this.form.get('search')?.value || '',
        sortCol: string = this.sort?.active || '',
        orderCol: string = this.sort?.direction.toUpperCase() || ''
    ): void {

        this.shipmentPriceSvc.get<IPaginatorProperties<ICarrierItem>>('carriers', page, limit, search, {}, sortCol, orderCol)
            .subscribe((data) => {
                if (!data) {
                    return;
                }
                this.carriersList = data.items;
                this.dataSource = new MatTableDataSource<ICarrierItem>(data.items);
                this.pageParams.pageSize = data.meta.itemsPerPage;
                this.pageParams.pageIndex = data.meta.currentPage - 1;
                this.pageParams.length = data.meta.totalItems;
            });
    }

    public pageChanged(e): void {
        this.getList(e.pageIndex + 1, e.pageSize);
    }

    public sortData(sort: Sort): void {
        const data = this.carriersList.slice();
        if (!sort.active || sort.direction === '') {
            this.carriersList = data;
            this.dataSource = new MatTableDataSource<ICarrierItem>(this.carriersList);
            return;
        }

        this.sort = sort;
        this.getList();
    }

    public onStatusChange(event: IStatus): void {
        this.showLoader = true;
        this.shipmentPriceSvc.updateStatus('carriers', event.id, event.status).subscribe((v) => {
            this.notify.showSuccessMessage(`Update successfully`);
            this.getList();
        }, (err) => {
            this.notify.showError(err.error.message);
            this.showLoader = false;
        });

    }

    public onDelete(id: number): void {
        this.showLoader = true;
        this.shipmentPriceSvc.delete('carriers', id).subscribe((v) => {
            this.notify.showSuccessMessage(`Delete successfully`);
            this.getList();
        }, (err) => {
            this.notify.showError(err.error.message);
            this.showLoader = false;
        });
    }

}
